import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormikWrapper } from "../../../components/formik_wrapper";
import * as actions from "../../../actions";
import { ADMIN, SUPERADMIN, FINANCEADMIN } from "../../../constants/roles";
import { Popup } from "../../../components/pop-up";
import { FlatButton, IconButton } from "../../../components/buttons";
import { ACL } from "../../../components/acl";
import FieldRenderer from "../../../components/policy_field_renderer";
import { zuper_connect_form as connectFieldsConfig } from "../../../util/master-data";
import { Banner } from "flowbite-react";
import AgentSetupModal from "./zuper_connect/agent_setup_modal";
import { PiSpinnerGapLight } from "react-icons/pi";

function ZuperConnect({ company_uid, companyDetails }) {
  const dispatch = useDispatch();

  const currentPolicy = useSelector((state) => state.company.policy);
  const connectState = useSelector((state) => state.connect);
  const isConnectSetup = connectState?.isConnectSetup;

  const connectForm = useFormikWrapper({
    initialValues: {},
    onSubmit: () => {
      setOpenModal("pop-up");
    },
  });

  const initForm = () => {
    // Extract zuper_connect data from policy
    const connectData = currentPolicy?.zuper_connect || {};
    connectForm.setValues({ zuper_connect: connectData });
  };

  const [openModal, setOpenModal] = useState();
  const [editMode, setEditMode] = useState(false);
  const [showAgentSetup, setShowAgentSetup] = useState(false);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);

  useEffect(() => {
    dispatch(actions.GETCOMPANYPOLICY_REQ({ company_uid }));

    return () => {
      dispatch(actions.RESET_STATE('connect', {
        isConnectSetup: null,
        provider: null,
        agents: null,
      }));
    };
  }, [company_uid]);

  useEffect(() => {
    setEditMode(false);
    initForm();
  }, [currentPolicy]);

  const handleEdit = () => {
    setOpenModal(undefined);
    dispatch(
      actions.EDITCOMPANYPOLICY_REQ({
        company_uid,
        formData: {
          ...currentPolicy,
          zuper_connect: connectForm.values.zuper_connect,
        },
      })
    );
  };

  useEffect(() => {
    if (company_uid) {
      dispatch(actions.GET_CONNECT_AGENT_REQ({
        company_uid
      }));
      dispatch(actions.GET_CONNECT_PROVIDER_REQ({
        company_uid
      }));
    }
  }, [company_uid]);

  // Watch for provider registration completion
  useEffect(() => {
    if (connectState?.provider?.telephony_provider_key && selectedAgentIds.length > 0) {
      dispatch(actions.ADD_CONNECT_AGENT_REQ({
        company_uid,
        user_uid: selectedAgentIds
      }));
      setSelectedAgentIds([]); // Clear after dispatching
    }
  }, [connectState, selectedAgentIds, company_uid, dispatch]);

  const handleAgentSetupConfirm = (selectedAgents) => {
    const user_uid = selectedAgents.map(agent => agent.user_uid);
    if (!connectState?.provider?.telephony_provider_key) {
      // First: register provider
      dispatch(actions.ADD_CONNECT_PROVIDER_REQ({
        company_uid,
        providerDetails: { providerKey: "TWILIO" }
      }));
      setSelectedAgentIds(user_uid); // Store selected agents for after registration
    } else {
      // Already registered: just add agents
      dispatch(actions.ADD_CONNECT_AGENT_REQ({ company_uid, user_uid }));
    }

    setShowAgentSetup(false);
  };

  return (
    <>
      <div className="h-full mt-3 rounded-lg">
        <div className="relative h-full w-full bg-white rounded-lg shadow">
          <div className="flow-root">
            <div
              className={`mx-auto pb-3 border-b sm:flex sm:items-center sm:justify-between ${(editMode && "sticky top-0") || ""
                } py-4 bg-white z-10 sm:px-6 lg:px-8 shadow rounded-t-lg`}
              rounded-t-lg
            >
              <h3 className="text-lg font-semibold leading-6 text-gray-900">
                Zuper Connect
              </h3>
              <ACL showFor={[SUPERADMIN, ADMIN, FINANCEADMIN]}>
                <div className="mt-3 sm:ml-4 sm:mt-0">
                  {(!editMode) && (
                    <div className="flex items-center justify-center space-x-3">
                      <IconButton onClick={() => setEditMode(true)} disabled={!isConnectSetup} className={`${!isConnectSetup ? "cursor-not-allowed" : "" }`}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-4 h-4"
                        >
                          <path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32L19.513 8.2z" />
                        </svg>
                      </IconButton>
                    </div>
                  )}
                  {editMode && (
                    <div className="flex items-center justify-center space-x-3">
                      <>
                        <FlatButton
                          appearance="secondary"
                          onClick={() => {
                            initForm();
                            setEditMode(false);
                          }}
                          className="w-[100px] inline-flex justify-center items-center gap-x-1.5"
                        >
                          Cancel
                        </FlatButton>
                        <FlatButton
                          className="w-[100px] inline-flex justify-center items-center gap-x-1.5"
                          onClick={() => {
                            connectForm.submitForm();
                          }}
                        >
                          Save
                        </FlatButton>
                      </>
                    </div>
                  )}
                </div>
              </ACL>
            </div>
            <div className="inline-block min-w-full py-4 align-middle sm:px-6 lg:px-8">
              {/* Setup Banner */}
              {
                connectState?.isConnectSetup == null ? (
                  <Banner className="mb-4">
                    <div className="flex w-full justify-between border-amber-200 bg-amber-50 p-2 rounded-lg">
                      <div className="mx-auto flex items-center">
                        <p className="flex items-center text-md font-normal text-amber-800 dark:text-amber-400">
                          <PiSpinnerGapLight className="mr-4 h-5 w-5 animate-spin" />
                          <span className="[&_p]:inline">
                            Checking configuration...
                          </span>
                        </p>
                      </div>
                    </div>
                  </Banner>
                ) : (!isConnectSetup ? (
                  <Banner className="mb-4">
                    <div className="flex w-full justify-between border-red-200 bg-red-50 p-3 rounded-lg">
                      <div className="mx-auto flex items-center">
                        <p className="flex items-center text-md font-normal text-red-500 dark:text-red-400">
                          <span className="[&_p]:inline">
                            No agents are configured in your Zuper Connect instance. Please complete the initial agent setup to continue.
                          </span>
                        </p>
                      </div>
                      <button
                        pill
                        className="border-1 bg-red-600 text-gray-50 hover:bg-red-700 shadow hover:shadow-inner px-3 py-1 rounded-lg"
                        onClick={() => setShowAgentSetup(true)}
                      >
                        <span className="text-sm font-medium">Setup</span>
                      </button>
                    </div>
                  </Banner>) : <></>)
              }

              <div className="relative mt-0">
                <div
                  className={`container mx-auto`}
                >
                  <div>
                    {connectFieldsConfig && connectFieldsConfig?.map((field, index) => (
                      <FieldRenderer
                        key={index}
                        field={field}
                        form={connectForm}
                        parentKey={null}
                        readOnly={!editMode}
                        currentPolicy={currentPolicy}
                      />
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
        <Popup
          modalState={[openModal, setOpenModal]}
          onConfirm={handleEdit}
          content={
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
              <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                Are you sure you want to edit the Zuper Connect settings of{" "}
                <b>{companyDetails?.company_name}</b>?
              </h3>
            </>
          }
        />
      </div>

      {/* Agent Setup Modal */}
      <AgentSetupModal
        company_uid={company_uid}
        isOpen={showAgentSetup}
        onClose={() => setShowAgentSetup(false)}
        onConfirm={handleAgentSetupConfirm}
      />
    </>
  );
}

export default ZuperConnect;
